import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type ChipTone = 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'neutral';

const TONE_ON: Record<ChipTone, string> = {
  primary: 'border-primary/30 bg-primary/10 text-primary',
  success: 'border-success/40 bg-success/10 text-success',
  danger: 'border-danger/40 bg-danger/10 text-danger',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  info: 'border-info/40 bg-info/10 text-info',
  neutral: 'border-foreground/20 bg-muted text-foreground',
};

const TONE_OFF = 'border-border bg-surface text-muted-foreground hover:text-foreground';

/**
 * A small pill that turns a filter or flag on and off.
 *
 * These were hand-rolled on every list page — same rounded pill, same
 * `border-primary/10 bg-primary/10` on state, subtly different padding each
 * time — and because they were plain `<button>`s none of them told a screen
 * reader whether they were on. `aria-pressed` comes for free here.
 *
 * Use `role="switch"` semantics via `Switch` instead when the control is a
 * settings toggle rather than a filter.
 */
export const ToggleChip = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
    active: boolean;
    tone?: ChipTone;
    icon?: ReactNode;
    children: ReactNode;
  }
>(function ToggleChip({ active, tone = 'primary', icon, className, children, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      aria-pressed={active}
      className={cn(
        'press inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] transition-colors focus-ring disabled:cursor-not-allowed disabled:opacity-60',
        active ? TONE_ON[tone] : TONE_OFF,
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});
